/**
 * Reusable data table component per BL-007, BL-008, BL-012.
 * Pagination, sorting, empty states, loading states, ARIA roles.
 */

import { Fragment, type ReactNode, useCallback } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { GlassCard } from "./glass-card";

export interface Column<T> {
  readonly key: string;
  readonly header: string;
  readonly sortable?: boolean;
  readonly className?: string;
  readonly render: (row: T) => ReactNode;
}

export type SortDirection = "asc" | "desc" | null;

export interface SortState {
  readonly column: string | null;
  readonly direction: SortDirection;
}

interface DataTableProps<T> {
  readonly columns: readonly Column<T>[];
  readonly data: readonly T[];
  readonly total?: number;
  readonly page?: number;
  readonly pageSize?: number;
  readonly onPageChange?: (page: number) => void;
  readonly onPageSizeChange?: (pageSize: number) => void;
  readonly sort?: SortState;
  readonly onSortChange?: (sort: SortState) => void;
  readonly emptyState?: ReactNode;
  readonly isLoading?: boolean;
  readonly rowKey: (row: T) => string;
  readonly onRowClick?: (row: T) => void;
  readonly expandedRowKey?: string | null;
  readonly renderExpandedRow?: (row: T) => ReactNode;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export function DataTable<T>({
  columns,
  data,
  total,
  page = 1,
  pageSize = 25,
  onPageChange,
  onPageSizeChange,
  sort,
  onSortChange,
  emptyState,
  isLoading,
  rowKey,
  onRowClick,
  expandedRowKey,
  renderExpandedRow,
}: DataTableProps<T>) {
  const handleSort = useCallback(
    (columnKey: string) => {
      if (!onSortChange) return;

      if (sort?.column === columnKey) {
        if (sort.direction === "asc") {
          onSortChange({ column: columnKey, direction: "desc" });
        } else if (sort.direction === "desc") {
          onSortChange({ column: null, direction: null });
        } else {
          onSortChange({ column: columnKey, direction: "asc" });
        }
      } else {
        onSortChange({ column: columnKey, direction: "asc" });
      }
    },
    [sort, onSortChange],
  );

  const totalItems = total ?? data.length;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  const totalPages = Math.ceil(totalItems / pageSize);

  if (isLoading) {
    return (
      <GlassCard className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-2 h-6 w-6 rounded-full bg-sc-primary animate-sc-pulse" />
          <p className="text-body-sm text-sc-text-muted">Loading...</p>
        </div>
      </GlassCard>
    );
  }

  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <GlassCard noPadding>
      <div className="overflow-x-auto">
        <table className="w-full" role="table">
          <thead>
            <tr
              className="border-b border-sc-border"
              role="row"
            >
              {columns.map((col) => (
                <th
                  key={col.key}
                  role="columnheader"
                  className={cn(
                    "px-4 py-3 text-left text-table-header font-semibold text-sc-text-muted uppercase tracking-wider",
                    col.sortable && "cursor-pointer select-none hover:text-sc-text",
                    col.className,
                  )}
                  onClick={
                    col.sortable ? () => handleSort(col.key) : undefined
                  }
                  aria-sort={
                    sort?.column === col.key && sort.direction
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                >
                  <div className="flex items-center gap-1">
                    {col.header}
                    {col.sortable && (
                      <span className="inline-flex">
                        {sort?.column === col.key ? (
                          sort.direction === "asc" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )
                        ) : (
                          <ChevronsUpDown
                            size={14}
                            className="opacity-40"
                          />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => {
              const key = rowKey(row);
              const isExpanded = expandedRowKey !== null && expandedRowKey !== undefined && expandedRowKey === key;
              return (
                <Fragment key={key}>
                  <tr
                    role="row"
                    className={cn(
                      "border-b border-sc-border/50 last:border-b-0",
                      onRowClick &&
                        "cursor-pointer hover:bg-sc-surface-hover transition-colors",
                      isExpanded && "bg-sc-surface-hover",
                    )}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        role="cell"
                        className={cn(
                          "px-4 py-3 text-body-sm text-sc-text",
                          col.className,
                        )}
                      >
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && renderExpandedRow && (
                    <tr className="border-b border-sc-border/50">
                      <td colSpan={columns.length} className="px-4 py-4 bg-sc-surface-hover/50">
                        {renderExpandedRow(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      {totalItems > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-sc-border">
          <p className="text-body-sm text-sc-text-muted">
            Showing {start}-{end} of {totalItems}
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label
                htmlFor="page-size"
                className="text-body-sm text-sc-text-muted"
              >
                Rows:
              </label>
              <select
                id="page-size"
                value={pageSize}
                onChange={(e) =>
                  onPageSizeChange?.(Number(e.target.value))
                }
                className="text-body-sm bg-sc-input-bg border border-sc-border rounded-input px-2 py-1 text-sc-text"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => onPageChange?.(page - 1)}
                disabled={page <= 1}
                className="px-2 py-1 text-body-sm rounded-btn border border-sc-border text-sc-text disabled:opacity-40 disabled:cursor-not-allowed hover:bg-sc-surface-hover transition-colors"
              >
                Previous
              </button>
              <span className="px-3 text-body-sm text-sc-text-muted">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => onPageChange?.(page + 1)}
                disabled={page >= totalPages}
                className="px-2 py-1 text-body-sm rounded-btn border border-sc-border text-sc-text disabled:opacity-40 disabled:cursor-not-allowed hover:bg-sc-surface-hover transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </GlassCard>
  );
}
